import type {
	ShareArtifactIdentity,
	ShareDeliveryChannel,
	ShareLinkRecord,
} from "@shared/types";
import { Check, Copy, MessageCircle, Share2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { StudioButton } from "@/features/studio/theme";
import type {
	StudioButtonSize,
	StudioButtonVariant,
} from "@/features/studio/theme/studio-button";
import { analytics } from "@/services/analytics.service";
import {
	createShareLink,
	recordShareEvent,
} from "../services/share-links-api.service";
import {
	buildShareMessage,
	buildWhatsAppUrl,
} from "../services/share-message.service";

interface ShareActionProps {
	artifact: ShareArtifactIdentity;
	label?: string;
	deliveryChannel?: ShareDeliveryChannel;
	variant?: StudioButtonVariant;
	size?: StudioButtonSize;
	className?: string;
	preferWebShare?: boolean;
}

async function copyToClipboard(value: string): Promise<void> {
	if (navigator.clipboard?.writeText) {
		await navigator.clipboard.writeText(value);
		return;
	}
	const textArea = document.createElement("textarea");
	textArea.value = value;
	document.body.appendChild(textArea);
	textArea.select();
	document.execCommand("copy");
	textArea.remove();
}

async function shareViaBrowser(
	shareLink: ShareLinkRecord,
	message: string,
): Promise<boolean> {
	if (!navigator.share || !shareLink.shareUrl) return false;
	await navigator.share({
		title: shareLink.summary.title,
		text: message,
		url: shareLink.shareUrl,
	});
	return true;
}

function shareIcon(
	deliveryChannel: ShareDeliveryChannel,
	copied: boolean,
): typeof Check {
	if (copied && deliveryChannel === "copy_link") return Check;
	if (deliveryChannel === "copy_link") return Copy;
	if (deliveryChannel === "whatsapp") return MessageCircle;
	return Share2;
}

export function ShareAction({
	artifact,
	label,
	deliveryChannel = "whatsapp",
	variant = "secondary",
	size = "sm",
	className,
	preferWebShare = true,
}: ShareActionProps): React.ReactElement {
	const { t } = useTranslation();
	const [isSharing, setIsSharing] = useState(false);
	const [copied, setCopied] = useState(false);

	const buttonLabel = label ?? t("sharing.share");

	const handleShare = async (): Promise<void> => {
		setIsSharing(true);
		setCopied(false);
		try {
			const result = await createShareLink({
				...artifact,
				deliveryChannel,
			});
			const { shareLink } = result;
			const shareUrl = shareLink.shareUrl ?? "";
			// Backend now returns whatsappMessage/whatsappUrl alongside the
			// link so agents can use the same message; the frontend prefers
			// those when present and falls back to local construction.
			const message =
				result.whatsappMessage ??
				buildShareMessage(shareLink.summary, shareUrl);
			const whatsappUrl = result.whatsappUrl ?? buildWhatsAppUrl(message);

			// recordShareEvent is best-effort analytics — never let an analytics
			// write failure mark the share as failed for the user.
			const fireAndForget = (
				eventType: "shared" | "copied",
				channel: "web_share" | "whatsapp" | "copy_link",
			): void => {
				// Product analytics first: recordShareEvent writes the share-link's
				// own per-token counter and returns early without a token, which is
				// a different question from "did anyone share anything".
				analytics.share.sent({ artifactType: artifact.type, channel });
				if (!shareLink.token) return;
				recordShareEvent(shareLink.token, { eventType, channel }).catch(() => {
					// best-effort; ignore
				});
			};

			if (preferWebShare) {
				const shared = await shareViaBrowser(shareLink, message).catch(
					() => false,
				);
				if (shared) {
					fireAndForget("shared", "web_share");
					toast.success(t("sharing.share"));
					return;
				}
			}

			if (deliveryChannel === "whatsapp") {
				window.open(whatsappUrl, "_blank", "noopener,noreferrer");
				fireAndForget("shared", "whatsapp");
				toast.success(t("sharing.whatsappOpened"));
				return;
			}

			await copyToClipboard(shareUrl);
			fireAndForget("copied", "copy_link");
			setCopied(true);
			toast.success(t("sharing.copied"));
		} catch {
			toast.error(t("sharing.failed"));
		} finally {
			setIsSharing(false);
		}
	};

	const Icon = shareIcon(deliveryChannel, copied);

	return (
		<StudioButton
			type="button"
			variant={variant ?? "secondary"}
			size={size ?? "sm"}
			className={className}
			disabled={isSharing}
			onClick={handleShare}
			icon={Icon}
		>
			{isSharing ? t("sharing.sharing") : buttonLabel}
		</StudioButton>
	);
}
